import { useState } from 'react';

import { useDatadog } from '~/lib/hooks/useDatadog';

const useLocalStorage = <T,>(
  key: string,
  initialValue: T,
): UseLocalStorageState<T> => {
  const { logError } = useDatadog();
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);

      /* TODO: evaluate items storing strategy and a consequent stringification.
       * For example, null shouldn't be stringifyed and undefined shouldn't be allowed.
       * It will require deeper investigation to understand what a better universal strategy would be.
       * Different local storage data are likely used in different patterns
       */
      const valueToStore = item ? JSON.parse(item) : initialValue;

      localStorage.setItem(key, JSON.stringify(valueToStore));

      return valueToStore;
    } catch (error) {
      console.error(
        `An error occurred parsing or storing localStorage key ${key}`,
        error,
      );
      logError(error, {
        description: `An error occurred parsing or storing localStorage key ${key}`,
      });
      localStorage.setItem(key, JSON.stringify(initialValue));
      return initialValue;
    }
  });

  const getValueFromLocalStorage = (): T => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : storedValue;
    } catch (error) {
      console.error(`An error occurred getting localStorage key ${key}`, error);
      logError(error, {
        description: `An error occurred getting localStorage key ${key}`,
      });
      return storedValue;
    }
  };

  const setValue = (value: T): void => {
    try {
      setStoredValue(value);
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`An error occurred storing localStorage key ${key}`, error);
      logError(error, {
        description: `An error occurred storing localStorage key ${key}`,
      });
      console.error(error);
    }
  };

  const removeValue = (): void => {
    try {
      setStoredValue(initialValue);
      localStorage.removeItem(key);
    } catch (error) {
      console.error(
        `An error occurred removing localStorage key ${key}`,
        error,
      );
      logError(error, {
        description: `An error occurred removing localStorage key ${key}`,
      });
      logError(error);
    }
  };

  return { getValueFromLocalStorage, removeValue, setValue, storedValue };
};

export default useLocalStorage;
